import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Labels, LegendBullet, PlainTooltip } from "@similarweb/ui-components";
import { Box, Text, Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import type { DomainShare, MainDomainToRegionalShares } from "app/pages/website-analysis/geography/types/GeoDataTypes";

const DomainsList = styled.ul`
    ${setFontVariant("tooltip", colorsPalettes.carbon[0])};
    list-style-type: disc;
    margin: 0 0 0 16px;
`;

const DomainsListItem = styled.li`
    margin-left: 4px;
`;

type TooltipContentProps = {
    datum: DomainShare[];
    maximumDomainsAmount: number;
    onViewCountryDomainsClick: () => void;
};

const TooltipContent = ({ datum, maximumDomainsAmount, onViewCountryDomainsClick }: TooltipContentProps) => {
    if (!datum) {
        return null;
    }

    const topEntries = datum.slice(0, maximumDomainsAmount);
    const otherEntries = datum.slice(maximumDomainsAmount);

    const othersSum = otherEntries.reduce((sum, entry) => sum + entry.totalShare, 0);

    const entries = topEntries.map((entry) => (
        <DomainsListItem key={entry.domain}>
            <Text fontVariant="tooltip">
                {translate("website.analysis.geography.regional.legend.tooltip.domainShare", {
                    domain: entry.domain,
                    share: percentageFilter()(entry.totalShare, 1),
                })}
            </Text>
        </DomainsListItem>
    ));

    const othersCount = otherEntries.length;
    const othersText = translate(
        othersCount === 1
            ? "website.analysis.geography.regional.legend.tooltip.others.singular"
            : "website.analysis.geography.regional.legend.tooltip.others",
        {
            countOfOtherDomains: othersCount,
            share: percentageFilter()(othersSum, 1),
        },
    );

    if (othersCount > 0) {
        entries.push(
            <DomainsListItem key="others">
                <Text fontVariant="tooltip">{othersText}</Text>
            </DomainsListItem>,
        );
    }

    return (
        <Box>
            <Text fontVariant="tooltip">
                {translate("website.analysis.geography.regional.legend.tooltip.header", {
                    domainsCount: datum.length,
                })}
            </Text>
            <DomainsList>{entries}</DomainsList>
            {othersCount > 0 ? (
                <Text
                    cursor="pointer"
                    fontVariant="tooltip"
                    color={colorsPalettes.blue[300]}
                    onClick={() => onViewCountryDomainsClick()}
                >
                    {translate("website.analysis.geography.regional.legend.tooltip.button.view")}
                </Text>
            ) : null}
        </Box>
    );
};

const MAX_DOMAINS_IN_TOOLTIP = 5;
const TOOLTIP_CLOSE_DELAY = 50;

interface IRegionalsLegendContainerProps {
    data: MainDomainToRegionalShares;
    onViewCountryDomainsClick: (parentDomainName: string, domains: DomainShare[]) => void;
}

const RegionalsLegendContainer = ({ data, onViewCountryDomainsClick }: IRegionalsLegendContainerProps) => {
    const chosenSitesLegends = useMemo(() => {
        return chosenSites.sitelistForLegend().map((site) => {
            const domainData = data[site.name];

            return (
                <PlainTooltip
                    key={site.name}
                    placement="bottom"
                    closeDelay={TOOLTIP_CLOSE_DELAY}
                    allowHover={true}
                    tooltipContent={
                        <TooltipContent
                            datum={domainData}
                            maximumDomainsAmount={MAX_DOMAINS_IN_TOOLTIP}
                            onViewCountryDomainsClick={() => onViewCountryDomainsClick(site.name, domainData)}
                        />
                    }
                >
                    <Flex
                        flexDirection="column"
                        gap="2px"
                    >
                        <LegendBullet
                            text={site.name}
                            labelColor={site.color}
                            dataLabel={Labels.Bullet}
                        />
                        <Text
                            fontVariant="caption"
                            padding="0 0 0 10px"
                        >
                            {domainData.length > 1
                                ? translate("website.analysis.geography.regional.legend.otherDomains.count", {
                                      count: domainData.length - 1,
                                  })
                                : null}
                        </Text>
                    </Flex>
                </PlainTooltip>
            );
        });
    }, [data, onViewCountryDomainsClick]);

    if (!data) {
        return null;
    }

    return <Flex padding="0 0 8px 0">{chosenSitesLegends}</Flex>;
};

export default RegionalsLegendContainer;
